import { useMemo } from 'react'
import { extractClubCodeFromPath } from '@/utils/fetchCurrentUser'
import { useMemberPortalApi } from './memberPortalApiClient'

function resolveClubCode(clubCode) {
  if (clubCode) return clubCode
  if (typeof window === 'undefined') return null
  return extractClubCodeFromPath(window.location?.pathname || '') || null
}

function normalizeRows(payload) {
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload)) return payload
  return []
}

function mapNotification(row) {
  return {
    id: row.id,
    category: row.category,
    type: row.type,
    title: row.title,
    body: row.body,
    createdAt: row.created_at,
    isRead: !!row.is_read,
    bookingId: row.booking_id,
    teeSlotId: row.tee_slot_id,
    payload: row.payload || {},
  }
}

export async function fetchMemberPortalNotifications(clubCode, { apiGet } = {}) {
  if (typeof apiGet !== 'function') {
    throw new Error('fetchMemberPortalNotifications requires an API client')
  }

  const code = resolveClubCode(clubCode)
  if (!code) return { ok: false, data: [] }

  const url = `/api/members-portal/${encodeURIComponent(code)}/notifications?limit=25`
  const res = await apiGet(url)
  const payload = await res.json().catch(() => ({}))
  if (!res.ok) {
    const errorMessage = payload?.error || `HTTP ${res.status}`
    throw new Error(errorMessage)
  }

  const rows = normalizeRows(payload)

  return {
    ok: true,
    data: rows.map(mapNotification),
    hasMore: Boolean(payload?.hasMore),
    nextCursor: payload?.nextCursor || null,
    unreadCount: Number(payload?.unreadCount) || 0,
  }
}

export async function fetchMemberPortalNotificationsPage(
  clubCode,
  { cursorCreatedAt = null, cursorId = null } = {},
  { apiGet } = {}
) {
  if (typeof apiGet !== 'function') {
    throw new Error('fetchMemberPortalNotificationsPage requires an API client')
  }

  const code = resolveClubCode(clubCode)
  if (!code) return { ok: false, data: [], hasMore: false, nextCursor: null }

  const params = new URLSearchParams({ limit: '25' })
  if (cursorCreatedAt && cursorId) {
    params.set('cursorCreatedAt', cursorCreatedAt)
    params.set('cursorId', cursorId)
  }

  const url = `/api/members-portal/${encodeURIComponent(code)}/notifications?${params.toString()}`
  const res = await apiGet(url)
  const payload = await res.json().catch(() => ({}))
  if (!res.ok) {
    const errorMessage = payload?.error || `HTTP ${res.status}`
    throw new Error(errorMessage)
  }

  const rows = normalizeRows(payload)
  return {
    ok: true,
    data: rows.map(mapNotification),
    hasMore: Boolean(payload?.hasMore),
    nextCursor: payload?.nextCursor || null,
    unreadCount: Number(payload?.unreadCount) || 0,
  }
}

export async function markNotificationsRead(ids, clubCode, { apiPost } = {}) {
  if (typeof apiPost !== 'function') {
    throw new Error('markNotificationsRead requires an API client')
  }

  const code = resolveClubCode(clubCode)
  if (!code) return { ok: false }

  const url = `/api/members-portal/${encodeURIComponent(code)}/notifications/read`
  const res = await apiPost(url, { ids: Array.isArray(ids) ? ids : [] })

  if (!res.ok) {
    const errorPayload = await res.json().catch(() => ({}))
    const errorMessage = errorPayload?.error || `HTTP ${res.status}`
    throw new Error(errorMessage)
  }

  const payload = await res.json().catch(() => ({}))
  return { ok: true, updatedCount: Number(payload?.updatedCount) || 0 }
}

export async function clearAllNotifications(clubCode, { apiPost } = {}) {
  if (typeof apiPost !== 'function') {
    throw new Error('clearAllNotifications requires an API client')
  }

  const code = resolveClubCode(clubCode)
  if (!code) return { ok: false }

  const url = `/api/members-portal/${encodeURIComponent(code)}/notifications/read`
  const res = await apiPost(url, {})

  if (!res.ok) {
    const errorPayload = await res.json().catch(() => ({}))
    const errorMessage = errorPayload?.error || `HTTP ${res.status}`
    throw new Error(errorMessage)
  }

  const payload = await res.json().catch(() => ({}))
  return { ok: true, updatedCount: Number(payload?.updatedCount) || 0 }
}

export function useMemberPortalNotificationsApi(options = {}) {
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi(options)

  return useMemo(
    () => ({
      fetchMemberPortalNotifications: (clubCode) =>
        fetchMemberPortalNotifications(clubCode, { apiGet: memberPortalApiGet }),
      fetchMemberPortalNotificationsPage: (clubCode, cursor) =>
        fetchMemberPortalNotificationsPage(clubCode, cursor, { apiGet: memberPortalApiGet }),
      markNotificationsRead: (ids, clubCode) =>
        markNotificationsRead(ids, clubCode, { apiPost: memberPortalApiPost }),
      clearAllNotifications: (clubCode) =>
        clearAllNotifications(clubCode, { apiPost: memberPortalApiPost }),
    }),
    [memberPortalApiGet, memberPortalApiPost]
  )
}
